import React, { useState, useMemo, useEffect } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import BigNumber from 'bignumber.js';
import { CheckBox } from 'grommet';
import { FormattedMessage, useIntl } from 'react-intl';
import { WorkflowPageTemplate } from '../../components/WorkflowPage/WorkflowPageTemplate';
import { Paper } from '../../components/Paper';
import { OperatingSystemButtons } from './OperatingSystemButtons';
import { Instructions } from './Instructions';
import { routeToCorrectWorkflowStep } from '../../utils/RouteToCorrectWorkflowStep';
import { StoreState } from '../../store/reducers';
import { Button } from '../../components/Button';
import { routesEnum } from '../../Routes';
import { Alert } from '../../components/Alert';
import { Link } from '../../components/Link';
import { Text } from '../../components/Text';
import { Heading } from '../../components/Heading';
import { NumberInput } from './NumberInput';
import {
  DispatchWorkflowUpdateType,
  updateWorkflow,
  WorkflowStep,
} from '../../store/actions/workflowActions';
import {
  MAX_EFFECTIVE_BALANCE,
  MIN_ACTIVATION_BALANCE,
  TICKER_NAME,
  LYXER_TO_GWEI,
} from '../../utils/envVars';
import instructions1 from '../../static/instructions_1.svg';
import instructions2 from '../../static/instructions_2.svg';
import { AccountTypeButtons } from './AccountTypeButtons';
import { toChecksumAddress } from 'web3-utils';

export enum operatingSystem {
  'MAC',
  'LINUX',
  'WINDOWS',
}

export type AccountType = '0x01' | '0x02';

const osMapping: { [os: number]: 'mac' | 'linux' | 'windows' } = {
  [operatingSystem.MAC]: 'mac',
  [operatingSystem.LINUX]: 'linux',
  [operatingSystem.WINDOWS]: 'windows',
};

export enum keysTool {
  'GUI',
  'CLI',
  'CLISOURCE',
}

const AddressInputContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const AddressInput = styled.input`
  height: 50px;
  flex: 1;
  font-size: 18px;
  line-height: 24px;
  color: #444444;
  padding-left: 10px;
  box-sizing: border-box;
  background-color: ${(p: any) => p.theme.gray.lightest};
  border-radius: ${(p: any) => p.theme.borderRadius};
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
  border: 1px solid #ddd;
  display: inline-flex;
  :focus {
    outline: none;
  }
`;

const AddressIndicator = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 2rem;
`;

const Highlight = styled.span`
  background: ${p => p.theme.green.medium};
`;

const InstructionImgContainer = styled.div`
  height: 250px;
  margin: 64px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
`;

// Prop definitions
interface OwnProps {}
interface StateProps {
  workflow: WorkflowStep;
}
interface DispatchProps {
  dispatchWorkflowUpdate: DispatchWorkflowUpdateType;
}
type Props = StateProps & DispatchProps & OwnProps;

const _GenerateKeysPage = ({
  dispatchWorkflowUpdate,
  workflow,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();

  const [stakedAmount, setStateStakedAmount] = useState<number>(
    MIN_ACTIVATION_BALANCE
  );
  const [validatorCount, setStateValidatorCount] = useState<number | string>(0);

  const [
    mnemonicAcknowledgementChecked,
    setMnemonicAcknowledgementChecked,
  ] = useState<boolean>(workflow > WorkflowStep.GENERATE_KEY_PAIRS);

  const [chosenType, setChosenType] = useState<AccountType>('0x02');
  const [chosenOs, setChosenOs] = useState<operatingSystem>(
    operatingSystem.LINUX
  );

  const [withdrawalAddress, setWithdrawalAddress] = useState<string>('');

  const defaultKeysTool = keysTool.GUI;
  const [chosenTool, setChosenTool] = useState<keysTool>(defaultKeysTool);

  const onCheckboxClick = (e: any) => {
    setMnemonicAcknowledgementChecked(e.target.checked);
  };

  const handleAddressChange = (e: any) => {
    // Only allow hexadecimal characters and 'x' (for 0x prefix)
    const re = /[^0-9a-fx]/gi;
    const value = e.target.value.replace(re, '');
    setWithdrawalAddress(value);
  };

  const isValidWithdrawalAddress = useMemo<boolean>(
    () => /^0x[0-9a-f]{40}$/i.test(withdrawalAddress),
    [withdrawalAddress]
  );

  useEffect(() => {
    if (!isValidWithdrawalAddress) return;
    setWithdrawalAddress(toChecksumAddress(withdrawalAddress));
  }, [isValidWithdrawalAddress, withdrawalAddress]);

  const addressIndicatorEmoji = useMemo<string>(() => {
    if (!withdrawalAddress) return '⬅';
    if (isValidWithdrawalAddress) return '✅';
    return '❌';
  }, [withdrawalAddress, isValidWithdrawalAddress]);

  const setValidatorCount = (count: number) => {
    const newCount = Math.max(count, 1);
    setStateValidatorCount(newCount);
    setStateStakedAmount(
      Math.min(newCount * MIN_ACTIVATION_BALANCE, MAX_EFFECTIVE_BALANCE)
    );
  };

  const setStakedAmount = (amount: number) => {
    const roundedToGwei = Math.floor(amount * LYXER_TO_GWEI) / LYXER_TO_GWEI;
    const newAmount = Math.min(
      MAX_EFFECTIVE_BALANCE,
      Math.max(1, roundedToGwei)
    );
    setStateStakedAmount(newAmount);
    setStateValidatorCount(Math.floor(newAmount / MIN_ACTIVATION_BALANCE));
  };

  const handleSubmit = () => {
    if (workflow === WorkflowStep.GENERATE_KEY_PAIRS) {
      dispatchWorkflowUpdate(WorkflowStep.UPLOAD_VALIDATOR_FILE);
    }
  };

  if (workflow < WorkflowStep.GENERATE_KEY_PAIRS) {
    return routeToCorrectWorkflowStep(workflow);
  }

  return (
    <WorkflowPageTemplate
      title={formatMessage({ defaultMessage: 'Generate key pairs' })}
    >
      <Paper className="mb20">
        <Heading level={2} size="small" color="blueMedium" className="mb20">
          <FormattedMessage defaultMessage="Withdrawal address" />
        </Heading>
        <Text className="mb20">
          <FormattedMessage
            defaultMessage="Your withdrawal address should be a regular Ethereum account that {youControl}, ideally using {coldStorage} techniques."
            values={{
              youControl: (
                <strong>
                  <FormattedMessage defaultMessage="you control" />
                </strong>
              ),
              coldStorage: (
                <em>
                  <FormattedMessage defaultMessage="cold storage" />
                </em>
              ),
            }}
          />
        </Text>
        <Text className="mb20">
          <FormattedMessage
            defaultMessage="Setting this address establishes your validator {withdrawalCredentials}, and permanently links the chosen execution address to your validator.
              This account will be the only account that can receive withdrawn funds from your validator."
            values={{
              withdrawalCredentials: (
                <em>
                  <FormattedMessage defaultMessage="withdrawal credentials" />
                </em>
              ),
            }}
          />
        </Text>
        <Text className="mb20">
          <FormattedMessage defaultMessage="It can also be used to authorize certain validator actions, such as requesting partial withdrawals, or exiting the validator account entirely." />
        </Text>
        <Text className="mb20">
          <FormattedMessage
            defaultMessage="Paste your chosen address here to include it in the copy/paste CLI
            command below:"
          />
        </Text>
        <AddressInputContainer className="mb40">
          <AddressInput
            onChange={handleAddressChange}
            value={withdrawalAddress}
            placeholder="0x..."
            maxLength={42}
          />
          <AddressIndicator>{addressIndicatorEmoji}</AddressIndicator>
        </AddressInputContainer>
        <Alert variant="error">
          {isValidWithdrawalAddress ? (
            <FormattedMessage
              defaultMessage="Make sure you have control over this address as this cannot be changed.
              Providing an account from a centralized exchange is not recommended. Cold storage is strongly recommended."
            />
          ) : (
            <FormattedMessage
              defaultMessage="Setting a withdrawal address is considered best security practice.
                If this is not provided now, your deposited funds will remain locked on the Beacon Chain until an address is provided.
                Unlocking will require signing a message with your BLS withdrawal keys, generated from your mnemonic seed phrase (so keep it safe)."
            />
          )}
        </Alert>
      </Paper>
      <Paper className="mt20">
        <Heading level={2} size="small" color="blueMedium">
          <FormattedMessage defaultMessage="What type of staking account?" />
        </Heading>
        <AccountTypeButtons
          chosenType={chosenType}
          setChosenType={setChosenType}
        />
      </Paper>

      {chosenType === '0x01' && (
        <Paper className="mt20">
          <Heading level={2} size="small" color="blueDark">
            <FormattedMessage defaultMessage="How many validators would you like to run?" />
          </Heading>
          <div className="flex mt20">
            <div>
              <Text className="mb5">
                <FormattedMessage defaultMessage="Validators" />
              </Text>
              <NumberInput
                value={validatorCount}
                setValue={setValidatorCount}
              />
            </div>
            <div className="ml50">
              <Text className="mb5">Cost</Text>
              <Text>
                {validatorCount === 0
                  ? validatorCount
                  : new BigNumber(validatorCount)
                      .times(new BigNumber(MIN_ACTIVATION_BALANCE))
                      .toFixed(0)
                      .toString()}{' '}
                {TICKER_NAME}
              </Text>
            </div>
          </div>

          {validatorCount > 1 ? (
            <Alert variant="info" className="mt40">
              <FormattedMessage
                defaultMessage="If you want to deposit more than one validator key, consider generating a validator with compounding withdrawal credentials (0x02 type)"
                description="Depositing large amount of validators"
              />
              <Link
                className="mt10"
                primary
                to="https://github.com/ethereum/EIPs/blob/master/EIPS/eip-7251.md"
              >
                <FormattedMessage defaultMessage="The EIP-7251 specification for a max effective validator balance." />
              </Link>
            </Alert>
          ) : (
            <></>
          )}
        </Paper>
      )}

      {chosenType === '0x02' && (
        <Paper className="mt20">
          <Heading level={2} size="small" color="blueDark">
            <FormattedMessage
              defaultMessage="How much {TICKER_NAME} would you like to stake?"
              values={{ TICKER_NAME }}
            />
          </Heading>
          <div className="flex mt20">
            <div>
              <Text className="mb5">
                <FormattedMessage defaultMessage="Amount" />
              </Text>
              <NumberInput value={stakedAmount} setValue={setStakedAmount} />
            </div>
            <div className="ml50">
              <Text className="mb5">Cost</Text>
              <Text>
                {stakedAmount} {TICKER_NAME}
              </Text>
            </div>
          </div>
        </Paper>
      )}

      <Paper className="mt20">
        <Heading level={2} size="small" color="blueMedium">
          <FormattedMessage defaultMessage="What is your current operating system?" />
        </Heading>
        <Text className="mt20 mb40">
          <FormattedMessage
            defaultMessage="Choose the OS of the computer you're currently using. This will be the
              computer you use to generate your keys. It doesn't need to be the OS
              you want to use for your node."
          />
        </Text>
        <OperatingSystemButtons chosenOs={chosenOs} setChosenOs={setChosenOs} />
      </Paper>

      <Instructions
        accountType={chosenType}
        validatorCount={validatorCount}
        stakedAmount={stakedAmount}
        withdrawalAddress={withdrawalAddress}
        os={osMapping[chosenOs]}
        chosenTool={chosenTool}
        setChosenTool={setChosenTool}
      />

      <Paper className="mt20">
        <Heading level={2} size="small" color="blueMedium">
          <FormattedMessage defaultMessage="Save the key files and get the validator file ready" />
        </Heading>
        <Text className="mt20">
          {chosenTool === keysTool.GUI ? (
            <FormattedMessage
              defaultMessage="You should now have your mnemonic written down in a safe place and a
              keystore saved for each of your {validatorCount} validators. Please
              make sure you keep these safe, preferably offline. Your validator
              keystores should be available in the selected directory."
              values={{
                validatorCount: <span>{validatorCount}</span>,
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="You should now have your mnemonic written down in a safe place and a
              keystore saved for each of your {validatorCount} validators. Please
              make sure you keep these safe, preferably offline. Your validator
              keystores should be available in the newly created
              {validatorKeys} directory."
              values={{
                validatorKeys: <Highlight>validator_keys</Highlight>,
                validatorCount: <span>{validatorCount}</span>,
              }}
            />
          )}
        </Text>
        <Alert variant="info" className="my40">
          <FormattedMessage
            defaultMessage="You should see that you have one keystore per validator. This keystore
              contains your signing key, encrypted with your password. You can use
              your mnemonic to generate your withdrawal key when you wish to
              withdraw."
          />
        </Alert>
        <InstructionImgContainer>
          <img src={instructions1} alt="" />
        </InstructionImgContainer>
        <Text>
          <FormattedMessage
            defaultMessage="The other file you just generated is
            {depositDataJson}. This file contains the
            public key(s) associated with your validator(s); You will need to
            upload this in the next step."
            values={{
              depositDataJson: <Highlight>deposit_data.json</Highlight>,
            }}
          />
        </Text>
        <InstructionImgContainer>
          <img src={instructions2} alt="" />
        </InstructionImgContainer>
        <Alert variant="error">
          <FormattedMessage
            defaultMessage="Warning: Do not store keys on multiple (backup) validators at once"
            description="Warns users to not run backup validators that have a live copy of their signing keys. Keys should only be on one validator machine at once."
          />
          <Link
            className="mt10"
            primary
            to="https://medium.com/prysmatic-labs/eth2-slashing-prevention-tips-f6faa5025f50"
          >
            <FormattedMessage defaultMessage="More on slashing prevention" />
          </Link>
        </Alert>
      </Paper>
      <Paper className="mt20">
        <CheckBox
          onChange={onCheckboxClick}
          checked={mnemonicAcknowledgementChecked}
          label={
            <Text>
              <FormattedMessage defaultMessage="I am keeping my key(s) safe and have written down my mnemonic phrase." />
            </Text>
          }
        />
      </Paper>

      <div className="flex center p30">
        <Link to={routesEnum.selectClient}>
          <Button
            className="mr10"
            width={100}
            label={formatMessage({ defaultMessage: 'Back' })}
          />
        </Link>
        <Link to={routesEnum.uploadValidatorPage} onClick={handleSubmit}>
          <Button
            width={300}
            rainbow
            disabled={!mnemonicAcknowledgementChecked}
            label={formatMessage({ defaultMessage: 'Continue' })}
          />
        </Link>
      </div>
    </WorkflowPageTemplate>
  );
};

const mapStateToProps = ({ workflow }: StoreState): StateProps => ({
  workflow,
});
const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatchWorkflowUpdate: (workflowStep: WorkflowStep) => {
    dispatch(updateWorkflow(workflowStep));
  },
});

export const GenerateKeysPage = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  StoreState
>(
  mapStateToProps,
  mapDispatchToProps
)(_GenerateKeysPage);
